/** Typed API client (paths and shapes generated from the backend's OpenAPI schema).
 * Every request carries the access token; a 401 triggers one refresh and one retry. */
import createClient from "openapi-fetch";
import { freshToken, refresh, setSession } from "./auth";
import type { paths } from "./schema";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public detail: string,
    public body: Record<string, unknown> = {},
  ) {
    super(detail || code);
  }
}

export async function authFetch(input: Request): Promise<Response> {
  const isAuth = new URL(input.url, location.origin).pathname.startsWith("/api/auth/");
  const retry = input.clone();
  const token = isAuth ? null : await freshToken();
  if (token) input.headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(input);
  if (res.status !== 401 || isAuth) return res;
  const s = await refresh();
  if (!s) {
    setSession(null, false);
    return res;
  }
  retry.headers.set("Authorization", `Bearer ${s.token}`);
  return fetch(retry);
}

export const api = createClient<paths>({ baseUrl: "", fetch: authFetch, credentials: "same-origin" });

async function toError(res: Response, body: unknown): Promise<ApiError> {
  const b = (body ?? {}) as Record<string, unknown>;
  const detail = typeof b.detail === "string" ? b.detail : res.statusText || "Request failed";
  return new ApiError(res.status, String(b.code ?? "error"), detail, b);
}

/** Unwrap an openapi-fetch result: the data, or a thrown ApiError. */
export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await p;
  if (!response.ok) throw await toError(response, error);
  return data as T;
}

/** Raw fetch with auth for endpoints that return binary (stills) or need headers back. */
export async function rawFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const req = new Request(path, { credentials: "same-origin", ...init });
  const res = await authFetch(req);
  if (!res.ok) {
    let body: unknown = {};
    try {
      body = await res.json();
    } catch {
      /* not json */
    }
    throw await toError(res, body);
  }
  return res;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.detail || e.code;
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}
